import { Dialog } from '@headlessui/react';
import { forwardRef, memo, PropsWithChildren, Ref, useEffect, useState } from 'react';
import { Editor, Transforms, Element as SlateElement, Range, Descendant } from 'slate';
import { useSlate } from 'slate-react';
import { BaseProps, Button, Icon, OrNull } from './components';
import { TbLink } from 'react-icons/tb';
import InputField from '../../InputField';

export type LinkElement = { type: 'link'; url: string; children: Descendant[] };
export type ButtonElement = { type: 'button'; children: Descendant[] };

export const withInlines = editor => {
  const { insertData, insertText, isInline } = editor;

  editor.isInline = element => ['link', 'button'].includes(element.type) || isInline(element);

  //   editor.insertText = text => {
  //     if (text) {
  //       wrapLink(editor, text);
  //     } else {
  //       insertText(text);
  //     }
  //   };

  //   editor.insertData = data => {
  //     const text = data.getData('text/plain');

  //     if (text) {
  //       wrapLink(editor, text);
  //     } else {
  //       insertData(data);
  //     }
  //   };

  return editor;
};

const insertLink = (editor, url) => {
  if (editor.selection) {
    wrapLink(editor, url);
  }
};

const insertButton = editor => {
  if (editor.selection) {
    wrapButton(editor);
  }
};

const isLinkActive = editor => {
  const array = Editor.nodes(editor, {
    at: Editor.unhangRange(editor, editor.selection),
    //@ts-ignore
    match: (n: any) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
  });
  return !!array;
};

const isButtonActive = editor => {
  const array = Editor.nodes(editor, {
    //@ts-ignore
    match: (n: any) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'button',
  });
  return !!array;
};

const unwrapLink = editor => {
  Transforms.unwrapNodes(editor, {
    //@ts-ignore
    match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
  });
};

const unwrapButton = editor => {
  Transforms.unwrapNodes(editor, {
    //@ts-ignore
    match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'button',
  });
};

const wrapLink = (editor, url: string) => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link: LinkElement = {
    type: 'link',
    url,
    children: isCollapsed ? [{ text: url }] : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
};

const wrapButton = editor => {
  if (isButtonActive(editor)) {
    unwrapButton(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const button: ButtonElement = {
    type: 'button',
    children: isCollapsed ? [{ text: 'Edit me!' }] : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, button);
  } else {
    Transforms.wrapNodes(editor, button, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
};

// Put this at the start and end of an inline component to work around this Chromium bug:
// https://bugs.chromium.org/p/chromium/issues/detail?id=1249405
const InlineChromiumBugfix = () => (
  <span contentEditable={false} className={''}>
    ${String.fromCodePoint(160) /* Non-breaking space */}
  </span>
);

export interface ILinkComponent extends BaseProps {
  attributes: any;
  element: any;
}

export const LinkComponent = forwardRef(
  ({ attributes, children, element }: PropsWithChildren<ILinkComponent>, ref: Ref<OrNull<HTMLSpanElement>>) => {
    return (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <a {...attributes} className={'underline cursor-pointer'} href={'#'}>
        {children}
      </a>
    );
  },
);

const EditableButtonComponent = ({ attributes, children }) => {
  return (
    /*
        Note that this is not a true button, but a span with button-like CSS.
        True buttons are display:inline-block, but Chrome and Safari
        have a bad bug with display:inline-block inside contenteditable:
        - https://bugs.webkit.org/show_bug.cgi?id=105898
        - https://bugs.chromium.org/p/chromium/issues/detail?id=1088403
        Worse, one cannot override the display property: https://github.com/w3c/csswg-drafts/issues/3226
        The only current workaround is to emulate the appearance of a display:inline button using CSS.
      */
    <span
      {...attributes}
      onClick={ev => ev.preventDefault()}
      // Margin is necessary to clearly show the cursor adjacent to the button
      className={''}
    >
      <InlineChromiumBugfix />
      {children}
      <InlineChromiumBugfix />
    </span>
  );
};

export const AddLinkButton = ({ onClick }) => {
  const editor = useSlate();

  // const offlineId = useAppSelector(selectOfflineId);
  // const action = useAppSelector(state => selectActionDAO(state, undefined));;

  return (
    <Button
      active={isLinkActive(editor)}
      onMouseDown={event => {
        event.preventDefault();
        onClick();
      }}
    >
      <Icon>
        <TbLink />
      </Icon>
    </Button>
  );
};

const RemoveLinkButton = () => {
  const editor = useSlate();

  return (
    <Button
      active={isLinkActive(editor)}
      onMouseDown={event => {
        if (isLinkActive(editor)) {
          unwrapLink(editor);
        }
      }}
    >
      <Icon>link_off</Icon>
    </Button>
  );
};

const ToggleEditableButtonButton = () => {
  const editor = useSlate();
  return (
    <Button
      active
      onMouseDown={event => {
        event.preventDefault();
        if (isButtonActive(editor)) {
          unwrapButton(editor);
        } else {
          insertButton(editor);
        }
      }}
    >
      <Icon>smart_button</Icon>
    </Button>
  );
};

export const LinkModal = memo(({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const editor = useSlate();
  const [link, setLink] = useState('');

  useEffect(() => {
    if (!open || !editor.selection) return;

    const [parentNode, parentPath] = Editor.parent(editor, editor.selection.focus?.path);
    //@ts-ignore
    setLink(parentNode.url);
  }, [editor, open]);

  const close = () => {
    if (link) insertLink(editor, link);

    onClose();
  };

  return (
    <Dialog
      as={'div'}
      className={
        'absolute top-1/2 left-1/2 transform text-white p-2 z-front -translate-x-1/2 -translate-y-1/2 rounded-md shadow-lg bg-slate-900'
      }
      open={open}
      onClose={close}
    >
      <Dialog.Title>
        <span>Enter the link address</span>
      </Dialog.Title>
      <Dialog.Panel>
        <InputField value={link} inputClassName="w-full" className="bg-black rounded m-2 w-60" onChange={setLink} />
      </Dialog.Panel>
    </Dialog>
  );
});
